import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw, { TwStyle } from 'twin.macro';

import { Loader } from '@components/Loader';
import { GenerateApiKeyForm } from '@account_module/components/GenerateApiKeyForm';
import { useSelfServeApiKeys } from '@account_module/hooks/useSelfServeApiKeys';
import { ISelfServeApiKey } from '@account_module/types/SelfServeApi';
import { CopyIcon } from '@icons/CopyIcon';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';
import { SearchIcon as _SearchIcon } from '@icons/SearchIcon';

const Container = tw.div`relative w-full overflow-auto border border-solid border-gray-3 p-[12px] bg-neutral-background rounded-[8px] shadow-ui`;
const Flex = tw.div`flex items-center justify-between`;
const FlexStart = tw(Flex)`justify-start`;
const Table = tw.div``;
const TableControls = tw(
  Flex
)`px-[16px] py-[10px] border border-solid border-gray-3 bg-gray-2 rounded-[6px]`;
const InputWrapper = tw.div`relative w-[316px] ml-[12px]`;
const SearchInput = tw.input`w-full h-[36px] border border-solid border-gray-1 bg-neutral-background rounded-[6px] px-[12px] py-[9px] text-[16px] placeholder:text-[16px] text-text-and-icon-muted placeholder:text-text-and-icon-muted leading-[110%] pl-[36px] transition-all duration-150 ease-in-out`;
const THead = tw.div`w-full bg-neutral-background`;
const TBody = tw.div`w-full`;
const TR = tw.div`w-full items-center grid gap-1 grid-cols-[300px_150px_150px] md:gap-[39px] px-[16px] rounded-[6px] [&:not(:last-child)]:mb-2`;
const BodyTR = tw(TR)`border border-solid border-gray-3 bg-page-background`;
const TD = tw.div`p-2 first:px-0 last:pr-0 text-[14px] font-medium text-text-and-icon`;
const ActionTD = tw(TD)`text-text-and-icon flex items-center justify-between`;
const HeadTD = tw(TD)`text-accent-muted whitespace-nowrap`;
const Title = tw.h3`text-text-and-icon text-[18px] font-semibold tracking-[0.18px] whitespace-nowrap`;
const ActionButton = tw.button`p-1 bg-neutral-background h-[32px] w-[32px] rounded-[4px] flex items-center justify-center shadow-ui border border-solid border-gray-1 mx-0`;
const SearchIcon = tw(
  _SearchIcon
)`stroke-text-and-icon-muted w-[18px] h-[18px] absolute left-[12px] top-1/2 translate-y-[-50%]`;
const iconStyle = tw`stroke-accent-1 w-[16px] h-[16px]` as TwStyle;

const Row = ({ item }: { item: ISelfServeApiKey }) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const handleCopyToClipboard = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(item.api_key);
      setIsCopied(true);
    }
  };
  const apiKey = `${item.api_key.substr(0, 3)}...${item.api_key.substr(
    item.api_key.length - 3,
    3
  )}`;

  const createdAt = item.created_at ? new Date(item.created_at).toLocaleDateString() : '-/-';

  useEffect(() => {
    if (!isCopied) return;

    const timeout = setTimeout(() => {
      setIsCopied(false);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [isCopied]);

  return (
    <BodyTR>
      <TD>{item.name}</TD>
      <ActionTD>
        <span>{apiKey}</span>
        <ActionButton onClick={handleCopyToClipboard} title='Copy to clipboard'>
          {isCopied ? <CheckMarkIcon css={[iconStyle]} /> : <CopyIcon css={[iconStyle]} />}
        </ActionButton>
      </ActionTD>
      <TD tw='text-center'>{createdAt}</TD>
    </BodyTR>
  );
};

export const ApiKeysTable = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useSelfServeApiKeys();
  const [query, setQuery] = useState<string>('');

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((item: ISelfServeApiKey) =>
      item.name.toLowerCase().includes(query.toLowerCase())
    );
  }, [data, query]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e?.target?.value);
  };
  return (
    <Container>
      <TableControls>
        <FlexStart>
          <Title>{t('creator_dashboard_module.self_serve_api_page.api_key_table.title')}</Title>
          <InputWrapper>
            {!query.length && <SearchIcon />}
            <SearchInput
              type='text'
              placeholder={t(
                'creator_dashboard_module.self_serve_api_page.api_key_table.input.placeholder'
              )}
              css={[query.length && tw`pl-[12px]`]}
              onChange={(e) => handleSearch(e as ChangeEvent<HTMLInputElement>)}
            />
          </InputWrapper>
        </FlexStart>
        <GenerateApiKeyForm />
      </TableControls>
      <Table>
        <THead>
          <TR>
            <HeadTD>
              {t('creator_dashboard_module.self_serve_api_page.api_key_table.column.name')}
            </HeadTD>
            <HeadTD>
              {t('creator_dashboard_module.self_serve_api_page.api_key_table.column.api_key')}
            </HeadTD>
            <HeadTD tw='text-center'>
              {t('creator_dashboard_module.self_serve_api_page.api_key_table.column.created_at')}
            </HeadTD>
          </TR>
        </THead>

        {isLoading ? (
          <Loader size='md' theme='dark' />
        ) : (
          <TBody>
            {filteredData?.map((item: ISelfServeApiKey, index: number) => (
              <Row key={index} item={item} />
            ))}
          </TBody>
        )}
      </Table>
    </Container>
  );
};
